import { createFileRoute, Link } from '@tanstack/react-router';
import { Briefcase, Download, Plus, Target, UserCog, Users } from 'lucide-react';

import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard, type StatCardProps } from '@/components/dashboard/StatCard';
import { JobsTrendChart } from '@/components/dashboard/JobsTrendChart';
import { JobsBySourceChart } from '@/components/dashboard/JobsBySourceChart';
import { ScrapingPipeline } from '@/components/dashboard/ScrapingPipeline';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { TopPerformers } from '@/components/dashboard/TopPerformers';
import { QuickActions } from '@/components/dashboard/QuickActions';
import { Button } from '@/components/ui/button';
import { useJobStats, useScrapingQueue } from '@/hooks/useJobs';
import { useEmployees } from '@/lib/hooks/useEmployees';

export const Route = createFileRoute('/admin/dashboard')({
  component: DashboardPage,
});

// Placeholder shown until a metric's query resolves (or if it errors) — keeps the
// tiles from collapsing while the numbers load.
const PLACEHOLDER = '—';

/** Week-over-week percentage change, rounded; null when there's no basis. */
function weekDelta(thisWeek?: number, lastWeek?: number): number | undefined {
  if (thisWeek === undefined || lastWeek === undefined) return undefined;
  if (lastWeek === 0) return thisWeek > 0 ? 100 : 0;
  return Math.round(((thisWeek - lastWeek) / lastWeek) * 100);
}

function useKpiCards(): StatCardProps[] {
  const { data: jobStats } = useJobStats();
  const { data: employees } = useEmployees();
  // Stage counts live on the scraping-queue response's `stats` block.
  const { data: queue } = useScrapingQueue();

  const num = (n?: number) => (n === undefined ? PLACEHOLDER : n.toLocaleString());

  return [
    {
      label: 'Total Jobs',
      value: num(jobStats?.total),
      icon: Briefcase,
      delta: weekDelta(jobStats?.thisWeekJobs, jobStats?.lastWeekJobs),
      hint: 'vs. last week',
    },
    {
      label: 'Active Customers',
      value: num(jobStats?.totalCustomers),
      icon: UserCog,
      hint: 'total on platform',
    },
    {
      label: 'Team Members',
      value: num(employees?.total),
      icon: Users,
      hint: 'active employees',
    },
    {
      label: 'Scrape Success',
      value:
        queue?.stats?.successRate === undefined
          ? PLACEHOLDER
          : `${queue.stats.successRate.toFixed(1)}%`,
      icon: Target,
      hint: 'scraping queue',
    },
  ];
}

function DashboardPage() {
  const stats = useKpiCards();

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Dashboard"
        description="Platform overview, pipeline health and quick actions."
        actions={
          <>
            <Button variant="outline" size="sm">
              <Download className="size-4" />
              Export
            </Button>
            <Button size="sm" asChild>
              <Link to="/admin/jobs/new">
                <Plus className="size-4" />
                New Job
              </Link>
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <JobsTrendChart />
        <JobsBySourceChart />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ScrapingPipeline />
        <RecentActivity />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <TopPerformers />
        <QuickActions />
      </div>
    </div>
  );
}
